"use client";

import { useState } from "react";
import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import type {
  FullWidthVideoFeatureMedia,
  FullWidthVideoFeatureVideo as FullWidthVideoFeatureVideoProps,
} from "./FullWidthVideoFeatureBlock.props";

const getAutoplayEmbedUrl = (url: string) => `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;

export const FullWidthVideoFeatureVideo = ({
  poster,
  video,
}: {
  poster?: FullWidthVideoFeatureMedia | null;
  video: FullWidthVideoFeatureVideoProps;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const Image = resolveKaajPrimitive("Image");
  const videoSource = video.embedUrl;
  const posterImage = poster ?? video.poster;

  if (!videoSource) {
    if (!posterImage?.defaultSrc) {
      return null;
    }

    return (
      <Image
        alt={posterImage.alternateText}
        className="aspect-video w-full object-cover"
        height={posterImage.height ?? undefined}
        sizes={posterImage.sizes ?? undefined}
        src={posterImage.defaultSrc}
        srcSet={posterImage.srcSet ?? undefined}
        width={posterImage.width ?? undefined}
      />
    );
  }

  if (posterImage?.defaultSrc && !isPlaying) {
    return (
      <button
        aria-label={`Play ${video.title}`}
        className="group relative block aspect-video w-full cursor-pointer overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-6px] focus-visible:outline-white"
        onClick={() => setIsPlaying(true)}
        type="button"
      >
        <Image
          alt={posterImage.alternateText}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out motion-reduce:transition-none motion-safe:group-hover:scale-[1.02]"
          height={posterImage.height ?? undefined}
          loading="lazy"
          sizes="100vw"
          src={posterImage.defaultSrc}
          srcSet={posterImage.srcSet ?? undefined}
          width={posterImage.width ?? undefined}
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,11,26,0.04)_0%,rgba(8,11,26,0.18)_45%,rgba(8,11,26,0.62)_100%)]"
        />
        <span className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full bg-[#2144FF] px-3 py-3 text-sm font-extrabold text-white shadow-[0_24px_58px_rgba(33,68,255,0.38)] ring-1 ring-white/45 transition-[background-color,box-shadow,transform] duration-200 group-hover:bg-[#132EDB] group-hover:shadow-[0_28px_68px_rgba(33,68,255,0.46)] motion-reduce:transition-none motion-safe:group-hover:scale-[1.02] sm:px-5">
          <span className="grid size-11 place-items-center rounded-full bg-white text-[#2144FF] shadow-[inset_0_0_0_1px_rgba(33,68,255,0.1)] sm:size-12">
            <span className="ml-1 h-0 w-0 border-y-[8px] border-l-[13px] border-y-transparent border-l-current" />
          </span>
          <span className="hidden sm:inline">Play video</span>
        </span>
        {video.title ? (
          <span
            className={cn(
              "absolute left-3 right-3 top-3 max-w-[calc(100%-1.5rem)] rounded-[1.1rem] border border-white/70 bg-white/94 px-3 py-2 text-[#151927] shadow-[0_16px_42px_rgba(8,11,26,0.16)]",
              "sm:left-5 sm:right-auto sm:top-5 sm:max-w-[min(28rem,calc(100%-2.5rem))] sm:rounded-2xl sm:px-5 sm:py-3",
            )}
          >
            <span className="block truncate text-xs font-extrabold tracking-[-0.01em] text-[#151927] sm:text-sm">
              {video.title}
            </span>
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <iframe
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      className="aspect-video w-full border-0"
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      src={isPlaying ? getAutoplayEmbedUrl(videoSource) : videoSource}
      title={video.title}
    />
  );
};
